import { useRef } from "react";
import { Camera, Images } from "lucide-react";

/**
 * Camera/gallery capture for the frosted-cake photo.
 *
 * The page owns upload state and the selected File. This component renders the
 * state beside the photo and keeps both capture paths available for retry.
 */
export default function FrosterPhotoCapture({
  previewSrc,
  hasSaved,
  uploading,
  disabled,
  inputKey,
  onFileSelected,
  onRetry,
  photoState = "ready",
  photoError = "",
  variant = "driver",
}) {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const state = uploading ? "uploading" : photoState || (hasSaved ? "saved" : "ready");

  const onChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || disabled || uploading) return;
    await onFileSelected(file);
  };

  const buttonClass =
    variant === "driver"
      ? "driver-btn driver-btn-dark"
      : "flex items-center justify-center gap-2 rounded-xl bg-[#1a1a1a] px-4 py-3.5 text-sm font-bold text-white disabled:opacity-50";
  const galleryClass =
    variant === "driver"
      ? "driver-btn driver-btn-teal"
      : "flex items-center justify-center gap-2 rounded-xl bg-teal-700 px-4 py-3.5 text-sm font-bold text-white disabled:opacity-50";
  const buttonStyle =
    variant === "driver"
      ? { display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }
      : undefined;

  const cameraBtn = (
    <button
      type="button"
      disabled={disabled || uploading}
      onClick={() => cameraRef.current?.click()}
      className={buttonClass}
      style={buttonStyle}
    >
      <Camera size={variant === "driver" ? 20 : 18} aria-hidden />
      {uploading ? "…" : hasSaved || previewSrc ? "Retake" : "Camera"}
    </button>
  );

  const galleryBtn = (
    <button
      type="button"
      disabled={disabled || uploading}
      onClick={() => galleryRef.current?.click()}
      className={galleryClass}
      style={buttonStyle}
    >
      <Images size={variant === "driver" ? 20 : 18} aria-hidden />
      Gallery
    </button>
  );

  const inputs = (
    <div key={inputKey}>
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        disabled={disabled || uploading}
        onChange={onChange}
        style={{ display: "none" }}
        aria-hidden
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        disabled={disabled || uploading}
        onChange={onChange}
        style={{ display: "none" }}
        aria-hidden
      />
    </div>
  );

  const statusText = {
    ready: "Ready — choose the frosted-cake photo.",
    uploading: "Uploading photo…",
    saved: "✓ Frosted photo saved",
    failed: `Not saved: ${photoError || "Upload failed — try again."}`,
  }[state] || "Ready — choose the frosted-cake photo.";

  const previewBlock = (
    <div style={{ textAlign: "center", marginBottom: 12 }}>
      <div
        aria-live="polite"
        className={
          variant === "driver"
            ? "driver-text-sm driver-font-medium driver-mb-3"
            : `mb-2 text-sm font-semibold ${state === "failed" ? "text-red-700" : "text-green-700"}`
        }
        style={variant === "driver" ? { color: state === "failed" ? "#B91C1C" : "#15803D" } : undefined}
      >
        {statusText}
      </div>
      {previewSrc ? (
        <img
          src={previewSrc}
          alt="Frosted cake"
          className={variant === "driver" ? "driver-door-thumb" : "mx-auto max-h-56 w-full rounded-2xl border border-green-200 object-cover"}
          loading="lazy"
          decoding="async"
        />
      ) : null}
      {state === "failed" ? (
        <button
          type="button"
          onClick={onRetry}
          disabled={disabled || uploading || !onRetry}
          className={variant === "driver" ? "driver-btn driver-btn-dark" : "mt-2 rounded-xl border border-red-300 bg-white px-4 py-2 text-sm font-bold text-red-700"}
        >
          Retry
        </button>
      ) : null}
    </div>
  );

  if (variant === "driver") {
    return (
      <div className="driver-card" style={{ borderColor: "#15803D", background: "#F0FDF4" }}>
        <div className="driver-label">Frosted cake photo</div>
        <p className="driver-call-hint" style={{ marginBottom: 8 }}>
          Take a photo of the frosted cake. Customer gets a compare link when you tap Done.
        </p>
        {previewBlock}
        {inputs}
        <div className="driver-grid-2">
          {cameraBtn}
          {galleryBtn}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-3xl border-2 border-green-300 bg-green-50 p-3">
      <p className="mb-1 text-base font-bold text-[#166534]">Frosted cake photo</p>
      <p className="mb-3 text-sm text-[#166534]/80">Take a photo of the frosted cake before marking done.</p>
      {previewBlock}
      {inputs}
      <div className="grid grid-cols-2 gap-2.5">
        {cameraBtn}
        {galleryBtn}
      </div>
    </div>
  );
}
